import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { AxiosErrorResponse } from '@/@types/Errors';
import { useAuth } from '@/auth/Auth';
import OnboardingSheet from '@/components/Onboarding/OnboardingSheet';
import { SecurityChallengeModal } from '@/components/shared/SecurityChallenge';
import { Button } from '@/components/ui/button';
import { SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { AuthService } from '@/services/auth.service';
import type { SecurityChallengePurpose } from '@/services/securityChallenge.service';

type SecurityStepProps = {
  open: boolean;
  onClose: () => void;
  loginTwoFactorEnabled?: boolean;
};

function extractErrorMessage(error: AxiosError<AxiosErrorResponse>) {
  const data = error.response?.data;
  if (data?.errors) {
    const firstError = Object.values(data.errors)[0];
    if (Array.isArray(firstError) && firstError[0]) {
      return String(firstError[0]);
    }
    if (typeof firstError === 'string') {
      return firstError;
    }
  }
  if (data?.message) {
    return String(data.message);
  }
  return '';
}

export function SecurityStep({
  open,
  onClose,
  loginTwoFactorEnabled = false,
}: SecurityStepProps) {
  const { t } = useTranslation(['onboarding', 'common']);
  const { authData, fetchUserData } = useAuth();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(loginTwoFactorEnabled);
  const [isChallengeOpen, setIsChallengeOpen] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState(loginTwoFactorEnabled);

  useEffect(() => {
    if (!open || isChallengeOpen) return;
    setEnabled(loginTwoFactorEnabled);
    setPendingEnabled(loginTwoFactorEnabled);
  }, [loginTwoFactorEnabled, open, isChallengeOpen]);

  const hasChanges = enabled !== loginTwoFactorEnabled;

  const challengePurpose = useMemo<SecurityChallengePurpose>(
    () =>
      pendingEnabled
        ? 'login_two_factor_enable'
        : 'login_two_factor_disable',
    [pendingEnabled],
  );

  const { mutate: confirmLoginTwoFactor, isPending } = useMutation({
    mutationFn: async ({
      enabled: nextEnabled,
      challengeToken,
    }: {
      enabled: boolean;
      challengeToken: string;
    }) =>
      AuthService.updateLoginTwoFactor({
        enabled: nextEnabled,
        challenge_token: challengeToken,
      }),
    onSuccess: async (_response, variables) => {
      await fetchUserData();
      await queryClient.invalidateQueries({ queryKey: ['user-info'] });
      toast({
        description: variables.enabled
          ? t('loginTwoFactorEnabledSuccess')
          : t('loginTwoFactorDisabledSuccess'),
        variant: 'success',
      });
      setIsChallengeOpen(false);
      onClose();
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      setEnabled(loginTwoFactorEnabled);
      setPendingEnabled(loginTwoFactorEnabled);
      toast({
        description:
          extractErrorMessage(error) ||
          t('loginTwoFactorUpdateError', { ns: 'common' }),
        variant: 'error',
      });
    },
  });

  const resetToggle = () => {
    setEnabled(loginTwoFactorEnabled);
    setPendingEnabled(loginTwoFactorEnabled);
  };

  const handleClose = () => {
    if (isPending || isChallengeOpen) return;
    resetToggle();
    onClose();
  };

  const handleSave = () => {
    if (!hasChanges || isPending) return;
    setPendingEnabled(enabled);
    setIsChallengeOpen(true);
  };

  const handleChallengeCancel = () => {
    setIsChallengeOpen(false);
  };

  const handleChallengeVerified = (challengeToken: string) => {
    setIsChallengeOpen(false);
    confirmLoginTwoFactor({
      enabled: pendingEnabled,
      challengeToken,
    });
  };

  return (
    <>
      <OnboardingSheet
        open={open}
        onClose={handleClose}
        hasHeader={false}
        blockClose={isChallengeOpen || isPending}
      >
        <div className="flex flex-col h-full">
          <div className="md:p-6 p-4 border-b border-colorBorder">
            <SheetTitle className="flex items-center gap-2 text-left">
              <ShieldCheck className="h-5 w-5 text-primary" />
              {t('security')}
            </SheetTitle>
            <p className="text-sm text-muted mt-2">
              {t('loginTwoFactorSettingsDescription')}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="rounded-xl border border-colorBorder bg-elevated/40 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-colorText">
                    {t('loginTwoFactorToggleTitle')}
                  </p>
                  <p className="text-xs text-muted leading-relaxed">
                    {t('loginTwoFactorToggleDescription')}
                  </p>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  disabled={isPending || isChallengeOpen}
                  data-cy="login-two-factor-toggle"
                />
              </div>
            </div>
          </div>

          <SheetFooter className="px-6 py-4 border-t border-colorBorder">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isPending}
            >
              {t('cancel', { ns: 'common' })}
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || isPending || isChallengeOpen}
              data-cy="login-two-factor-save"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('saving', { ns: 'common' })}
                </>
              ) : (
                t('save', { ns: 'common' })
              )}
            </Button>
          </SheetFooter>
        </div>
      </OnboardingSheet>

      <SecurityChallengeModal
        open={isChallengeOpen}
        purpose={challengePurpose}
        email={authData?.email}
        onVerified={handleChallengeVerified}
        onCancel={handleChallengeCancel}
      />

      {isPending && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
    </>
  );
}
